/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { AccountService } from '../../services/account.service';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { ErrorService } from '../../services/error.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { DriftService } from '../../services/drift.service';

import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from '../../global/alert-dialog/alert-dialog.component';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { clone, filter, sort } from 'ramda';

@Component({
  selector: 'app-settings-users',
  templateUrl: './settings-users.component.html'
  // host: { '[@routerTransition]': 'true' },
  // animations: Animations.page
})
export class SettingsUsersComponent implements OnInit {

  loadingPage: boolean = true;
  users: any[] = [];
  session: any = null;

  userDialogRef: BsModalRef;
  userForm: FormGroup;
  emailBlur: boolean = false;

  roles: any[] = [
    { title: 'user', mask: 4, display: 'User' },
    { title: 'manager', mask: 8, display: 'Manager' },
    { title: 'admin', mask: 16, display: 'Admin' }
  ];

  dTitle: string = '';
  dMessage: string = '';
  dUserId: string = null;

  myId: string;

  constructor(private authService: AuthService,
              private accountService: AccountService,
              private userService: UserService,
              private cacheService: CacheService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private driftService: DriftService,
              private modalService: BsModalService,
              private fb: FormBuilder,
              private globalMessageService: GlobalMessageService) {
    this.userForm = fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(1)])],
      email: ['', Validators.compose([Validators.required, Validators.minLength(1), Validators.pattern('[a-zA-Z0-9+-._]+(\.[_a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,15})')])],
      role: [this.roles[1], Validators.compose([Validators.required, Validators.minLength(1)])]
    });
  }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      );
    }
    else {
      this.initView();
    }
  }

  newUser(dialog) {
    if (!this.session.f.okta) {
      this.dTitle = 'Add User';
      this.dMessage = 'Use this form to add a user to your Drift Email account. They will be sent a welcome email with login instructions. All fields are required.';
      this.dUserId = null;
      this.initUserForm(); // just in case they had previously edited a user - clear the form
      this.userDialogRef = this.modalService.show(dialog, { class: 'modal-md' });
    }
  }

  saveUser() {
    this.userDialogRef.hide();

    if (this.dUserId) {
      this.saveExistingUser();
    }
    else {
      this.saveNewUser();
    }
  }

  saveNewUser() {
    let u: any = {
      name: this.userForm.controls['name'].value,
      email_address: this.userForm.controls['email'].value.toLowerCase(),
      account_role: {
        account_id: this.cacheService.get('session').account_id,
        account_name: this.cacheService.get('session').account,
        role: {
          title: this.userForm.controls['role'].value.title,
          mask: this.userForm.controls['role'].value.mask
        }
      }
    };

    let existing = false;
    this.users.forEach((cUser) => {
      if (cUser.email_address == u.email_address) {
        existing = true;
      }
    });

    if (!existing) {
      this.userService.addUser(u).subscribe(
        (result: any) => {
          this.globalMessageService.message('New user was added to your account');
          this.driftService.track('added-user', { email: u.email_address });
          this.loadUsers();
        },
        (err) => {
          this.globalMessageService.error();
          this.errorService.log(err, 'settings-users.component', 'newUser');
        }
      );
    }
    else {
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `A user with the email address ${u.email_address} already exists in this account. You can change user settings by clicking the "Edit" button.`,
          confirmBtn: 'Ok'
        }
      }));
    }
  }

  saveExistingUser() {
    let session = this.cacheService.get('session');
    let u: any = {
      _id: this.dUserId,
      name: this.userForm.controls['name'].value,
      email_address: this.userForm.controls['email'].value.toLowerCase(),
      account_role: {
        account_id: session.account_id,
        account_name: session.account,
        role: {
          title: this.userForm.controls['role'].value.title,
          mask: this.userForm.controls['role'].value.mask
        }
      }
    };

    this.userService.updateUser(u).subscribe(
      (result: any) => {
        this.globalMessageService.message('Changes to ' + u.name + ' were saved');
        this.loadUsers();
      },
      (err) => {
        this.globalMessageService.error();
        this.errorService.log(err, 'settings-users.component', 'editUser');
      }
    );
  }

  editUser(dialog, user) {
    if (!this.session.f.okta) {

      this.dTitle = 'Edit User';
      this.dMessage = 'Use this form to edit a user';
      this.dUserId = user._id;
      this.userForm.controls['name'].setValue(user.name);
      this.userForm.controls['email'].setValue(user.email_address);
      this.userForm.controls['role'].setValue(user.role);
      this.userDialogRef = this.modalService.show(dialog, { class: 'modal-md' });
    }
  }

  async reInviteUser(user) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Please confirm you would like to re-invite ${user.name} to your account. This action will send another invite email.`,
        confirm:  () => {
          me.userService.reInviteUser(user._id, this.myId).subscribe(
            (result: any) => {
              me.globalMessageService.message(`${user.name} was re-invited to your account`);
              me.driftService.track('re-invited-user', { email: user.email_address });
              me.loadUsers();
            },
            (err) => {
              me.globalMessageService.error();
              me.errorService.log(err, 'settings-users.component', 'editUser');
            });
        }
      }
    }));
  }

  /**
   * Special validation for email since we don't want validation until blur, and we want to trim whitespace
   * for copy/paste bugs
   */
  trimEmail() {
    this.userForm.controls['email'].setValue(this.userForm.controls['email'].value.trim());
    this.emailBlur = true;
  }

  // This method is only used in context with SSO enabled logins. Since we dont want to delete the user,
  // we just modify the email to contain a DELETE prepend and then set a field to filter them out of the view
  private modDeleteUser = (user: any) => {
    const clonedUser = clone(user);
    clonedUser.email_address = `DELETED_${user.email_address}`;
    clonedUser.is_archived = true;
    const copyRole = user.role;

    clonedUser.account_role = {
      role: {
        ...copyRole
      }
    };

    delete clonedUser.role;
    const internalThis = this;

    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Please confirm you would like to remove ${user.name} from your account.`,
        confirm: () => {
          internalThis.userService.updateUser(clonedUser).subscribe((res: any) => {
            internalThis.globalMessageService.message(`${user.name} was removed from your account`);
            internalThis.driftService.track('mod-deleted-user', {email: user.email_address});
            internalThis.userService.deleteUser(user._id).subscribe((res: any) => internalThis.loadUsers());
          }, (err: any) => {
            internalThis.globalMessageService.error();
            internalThis.errorService.log(err, 'settings-users.component', 'editUser');
          });
        }
      }
    }));
  }

  private deleteUser(user) {
    if (!this.session.f.okta) {
      let me = this;
      this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
        initialState: {
          message: `Please confirm you would like to remove "${user.name}" from your account`,
          confirm: () => {
            me.userService.deleteUser(user._id).subscribe(
              (result: any) => {
                me.globalMessageService.message(user.name + ' was removed from your account');
                me.driftService.track('deleted-user', { email: user.email_address });
                me.loadUsers();
              },
              (err) => {
                me.globalMessageService.error();
                me.errorService.log(err, 'settings-users.component', 'editUser');
              });
          }
        }
      }));
    }
  }

  private async initView() {
    // filter roles down to only those allowed for this user
    this.roles = this.roles.filter((r) => {
      return r.mask <= this.authService.currentRole.mask;
    });
    this.myId = await this.authService.getCurrentUserId();

    this.loadUsers();
  }

  private loadUsers() {
    this.loadingPage = true;
    this.accountService.getUsers().subscribe(
      (result: any) => {
        this.users = result;
        for (let u of this.users) {
          for (let r of this.roles) {
            if (u.role.mask == r.mask) {
              u.role = r;
            }
          }
        }
        const sortByName = (a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1;
        this.users = filter((user: any) => !user.is_archived, this.users).sort(sortByName, this.users);
        this.loadingPage = false;
      },
      (err) => {
        this.globalMessageService.error();
        this.errorService.log(err, 'settings-users.component', 'loadUsers');
      }
    );
  }

  private initUserForm() {
    this.userForm.controls['name'].setValue('');
    this.userForm.controls['email'].setValue('');
    this.userForm.controls['role'].setValue(this.roles[0]);
    this.userForm.markAsPristine();
  }
}



// WEBPACK FOOTER //
// ./client/app/settings/settings-users/settings-users.component.ts